import React, { useState } from 'react';

import Button from './Button';

const QuickActionButton = ({ className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getCurrentPath = () => {
    return window.location?.pathname;
  };

  const getContextualActions = () => {
    const currentPath = getCurrentPath();
    
    const actionMappings = {
      '/articles-management': [
        { 
          label: 'New Article', 
          path: '/article-editor', 
          icon: 'FileText',
          primary: true,
          description: 'Create a new article'
        },
        { 
          label: 'Import Articles', 
          path: '/articles-import', 
          icon: 'Upload',
          description: 'Import articles from file'
        }
      ],
      '/projects-management': [
        { 
          label: 'New Project', 
          path: '/project-form', 
          icon: 'FolderPlus',
          primary: true,
          description: 'Create a new project'
        },
        { 
          label: 'Project Template', 
          path: '/project-template', 
          icon: 'Copy',
          description: 'Use project template'
        }
      ],
      '/sub-unions-management': [
        { 
          label: 'New Sub-Union', 
          path: '/sub-union-form', 
          icon: 'Building2',
          primary: true,
          description: 'Create a new sub-union'
        },
        { 
          label: 'Import Sub-Unions', 
          path: '/sub-unions-import', 
          icon: 'Upload',
          description: 'Import sub-unions from file'
        }
      ],
      '/cities-management': [
        { 
          label: 'Add City', 
          path: '/city-form', 
          icon: 'MapPin',
          primary: true,
          description: 'Add a new city'
        },
        { 
          label: 'Import Cities', 
          path: '/cities-import', 
          icon: 'Upload',
          description: 'Import cities from file'
        }
      ],
      '/offices-management': [
        { 
          label: 'New Office', 
          path: '/office-form', 
          icon: 'Briefcase',
          primary: true,
          description: 'Create a new office position'
        },
        { 
          label: 'Office Template', 
          path: '/office-template', 
          icon: 'Copy',
          description: 'Use office template'
        }
      ],
      '/dashboard': [
        { 
          label: 'Quick Article', 
          path: '/article-editor', 
          icon: 'FileText',
          primary: true,
          description: 'Create new article'
        },
        { 
          label: 'New Project', 
          path: '/project-form', 
          icon: 'FolderPlus',
          description: 'Create new project'
        },
        { 
          label: 'Add Sub-Union', 
          path: '/sub-union-form', 
          icon: 'Building2',
          description: 'Create new sub-union'
        }
      ]
    };

    return actionMappings?.[currentPath] || [];
  };

  const actions = getContextualActions();

  if (actions?.length === 0) {
    return null;
  }

  const handleNavigation = (path) => {
    window.location.href = path;
    setIsExpanded(false);
  };

  const handleToggle = () => {
    if (actions?.length === 1) {
      handleNavigation(actions?.[0]?.path);
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  const primaryAction = actions?.find(action => action?.primary) || actions?.[0];

  return (
    <div className={`fixed bottom-6 right-6 z-300 ${className}`}>
      {/* Expanded Actions */}
      {isExpanded && actions?.length > 1 && (
        <div className="mb-4 space-y-3 animate-slide-up">
          {actions?.slice(1)?.reverse()?.map((action, index) => (
            <div
              key={action?.path}
              className="flex items-center justify-end space-x-3"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="bg-popover border border-border rounded-lg px-3 py-2 shadow-elevation-2 animate-fade-in">
                <p className="text-sm font-medium text-popover-foreground">
                  {action?.label}
                </p>
                <p className="text-xs text-muted-foreground">
                  {action?.description}
                </p>
              </div>
              
              <Button
                variant="secondary"
                size="icon"
                className="w-12 h-12 shadow-elevation-2 hover:shadow-elevation-3 transition-all duration-200"
                onClick={() => handleNavigation(action?.path)}
                iconName={action?.icon}
                iconSize={20}
                title={action?.label}
              />
            </div>
          ))}
        </div>
      )}
      {/* Primary Action Button */}
      <div className="flex items-center justify-end space-x-3">
        {isExpanded && (
          <div className="bg-popover border border-border rounded-lg px-3 py-2 shadow-elevation-2 animate-fade-in">
            <p className="text-sm font-medium text-popover-foreground">
              {primaryAction?.label}
            </p>
            <p className="text-xs text-muted-foreground">
              {primaryAction?.description}
            </p>
          </div>
        )}
        
        <div className="relative">
          <Button
            variant="default"
            size="icon"
            className="w-14 h-14 bg-primary hover:bg-primary/90 shadow-elevation-2 hover:shadow-elevation-3 transition-all duration-200 animate-spring"
            onClick={handleToggle}
            iconName={isExpanded ? 'X' : primaryAction?.icon}
            iconSize={24}
            title={primaryAction?.label}
          />
          
          {actions?.length > 1 && !isExpanded && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-secondary text-secondary-foreground rounded-full flex items-center justify-center text-xs font-medium shadow-sm">
              {actions?.length}
            </div>
          )}
        </div>
      </div>
      {/* Backdrop */}
      {isExpanded && (
        <div
          className="fixed inset-0 z-[-1] bg-background/20 backdrop-blur-sm"
          onClick={() => setIsExpanded(false)}
        />
      )}
    </div>
  );
};

export default QuickActionButton;